import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Plus, Building2, Users, Calendar, Mail, Trash2, LogOut, FileEdit as Edit3, RotateCcw, Bell, CheckCircle, XCircle, Clock, User, HelpCircle, FlaskConical, LayoutDashboard, Calculator, KeyRound, Download } from 'lucide-react';
import { FormulasModal } from '../components/FormulasModal';
import { HelpDocModal } from '../components/HelpDocModal';
import { CRMModal } from '../components/CRMModal';
import { SimulatorModal } from '../components/simulator/SimulatorModal';
import { AccessManagementModal } from '../components/AccessManagementModal';

interface SharingOperation {
  id: string;
  name: string;
  description: string;
  created_at: string;
  deleted_at?: string | null;
  participants_count?: number;
  pending_requests_count?: number;
}


interface PendingRequest {
  id: string;
  type: 'participant' | 'manager' | 'member_account';
  email: string;
  name?: string;
  ean_code?: string;
  operation_id: string;
  operation_name: string;
  request_date: string;
  account_id?: string;
}

export function SharingOperationsPage() {
  const navigate = useNavigate();
  const [operations, setOperations] = useState<SharingOperation[]>([]);
  const [deletedOperations, setDeletedOperations] = useState<SharingOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState<SharingOperation | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const [newOperationName, setNewOperationName] = useState('');
  const [newOperationDescription, setNewOperationDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [showFormulasModal, setShowFormulasModal] = useState(false);
  const [showHelpDoc, setShowHelpDoc] = useState(false);
  const [showCRM, setShowCRM] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [filteredOperationId, setFilteredOperationId] = useState<string | null>(null);
  const [backingUp, setBackingUp] = useState(false);

  useEffect(() => {
    loadOperations();
  }, []);

  const loadOperations = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }

      const superAdmin = user.email === 'info@sunisup.be';
      setIsSuperAdmin(superAdmin);

      const { data: ops, error: opsError } = await supabase
        .from('sharing_operations')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (opsError) throw opsError;

      const opsWithCounts = await Promise.all(
        (ops || []).map(async (op) => {
          const { count } = await supabase
            .from('participants')
            .select('*', { count: 'exact', head: true })
            .eq('sharing_operation_id', op.id);

          const { count: pendingParticipants } = await supabase
            .from('participants')
            .select('*', { count: 'exact', head: true })
            .eq('sharing_operation_id', op.id)
            .eq('account_status', 'pending');

          const { count: pendingManagers } = await supabase
            .from('sharing_operation_admins')
            .select('*', { count: 'exact', head: true })
            .eq('sharing_operation_id', op.id)
            .eq('account_status', 'pending');

          const { count: pendingMemberAccounts } = await supabase
            .from('participant_accounts')
            .select('*, participants!inner(sharing_operation_id)', { count: 'exact', head: true })
            .eq('participants.sharing_operation_id', op.id)
            .eq('account_status', 'pending');

          return {
            ...op,
            participants_count: count || 0,
            pending_requests_count: (pendingParticipants || 0) + (pendingManagers || 0) + (pendingMemberAccounts || 0),
          };
        })
      );

      setOperations(opsWithCounts);

      if (superAdmin) {
        await loadPendingRequests(ops || []);
      }

      if (superAdmin) {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        const { data: deletedOps, error: deletedError } = await supabase
          .from('sharing_operations')
          .select('*')
          .not('deleted_at', 'is', null)
          .gte('deleted_at', oneWeekAgo.toISOString())
          .order('deleted_at', { ascending: false });

        if (!deletedError && deletedOps) {
          const deletedOpsWithCounts = await Promise.all(
            deletedOps.map(async (op) => {
              const { count } = await supabase
                .from('participants')
                .select('*', { count: 'exact', head: true })
                .eq('sharing_operation_id', op.id);
              return { ...op, participants_count: count || 0 };
            })
          );
          setDeletedOperations(deletedOpsWithCounts);
        }
      }
    } catch (error: any) {
      console.error('Erreur chargement opérations:', error);
      toast.error('Erreur lors du chargement des opérations');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async (ops: any[]) => {
    const allRequests: PendingRequest[] = [];

    for (const op of ops) {
      const { data: pendingParticipants } = await supabase
        .from('participants')
        .select('id, email, name, ean_code, account_request_date')
        .eq('sharing_operation_id', op.id)
        .eq('account_status', 'pending');

      if (pendingParticipants) {
        const seenEmails = new Set<string>();
        for (const p of pendingParticipants) {
          const key = `${(p.email || '').toLowerCase()}_${op.id}`;
          if (seenEmails.has(key)) continue;
          seenEmails.add(key);
          allRequests.push({
            id: p.id,
            type: 'participant',
            email: p.email || '',
            name: p.name,
            ean_code: p.ean_code,
            operation_id: op.id,
            operation_name: op.name,
            request_date: p.account_request_date || '',
          });
        }
      }

      const { data: pendingManagers } = await supabase
        .from('sharing_operation_admins')
        .select('id, email, account_request_date')
        .eq('sharing_operation_id', op.id)
        .eq('account_status', 'pending');

      if (pendingManagers) {
        for (const m of pendingManagers) {
          allRequests.push({
            id: m.id,
            type: 'manager',
            email: m.email,
            operation_id: op.id,
            operation_name: op.name,
            request_date: m.account_request_date || '',
          });
        }
      }

      const pendingParticipantEmails = new Set(
        (pendingParticipants || []).map(p => (p.email || '').toLowerCase())
      );

      const { data: pendingMemberAccounts } = await supabase
        .from('participant_accounts')
        .select('id, email, account_request_date, participant_id, participants!inner(name, ean_code, sharing_operation_id)')
        .eq('account_status', 'pending')
        .eq('participants.sharing_operation_id', op.id);

      if (pendingMemberAccounts) {
        const seenAccountEmails = new Set<string>();
        for (const a of pendingMemberAccounts) {
          const email = (a.email || '').toLowerCase();
          if (pendingParticipantEmails.has(email) || seenAccountEmails.has(email)) continue;
          seenAccountEmails.add(email);
          const participant = (a as any).participants;
          allRequests.push({
            id: a.id,
            type: 'member_account',
            email: a.email || '',
            name: participant?.name,
            ean_code: participant?.ean_code,
            operation_id: op.id,
            operation_name: op.name,
            request_date: a.account_request_date || '',
            account_id: a.id,
          });
        }
      }
    }

    allRequests.sort((a, b) => new Date(b.request_date).getTime() - new Date(a.request_date).getTime());
    setPendingRequests(allRequests);
  };

  const handleApproveRequest = async (request: PendingRequest) => {
    setProcessingRequest(request.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (request.type === 'participant') {
        // Approve all participants with the same email in the same operation (group members)
        const { data: approvedRows, error } = await supabase
          .from('participants')
          .update({ account_status: 'approved' })
          .eq('sharing_operation_id', request.operation_id)
          .eq('email', request.email)
          .select('id');

        if (error) throw error;

        const approvedIds = (approvedRows || []).map(r => r.id);
        if (approvedIds.length > 0) {
          const { error: accountsError } = await supabase
            .from('participant_accounts')
            .update({ account_status: 'approved' })
            .eq('email', request.email.toLowerCase())
            .in('participant_id', approvedIds);
          if (accountsError) throw accountsError;
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();
        const approvalResponse = await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'send_approval_email',
            email: request.email,
            name: request.name,
            type: 'participant',
            operation_name: request.operation_name,
          }),
        });

        if (!approvalResponse.ok) {
          const errText = await approvalResponse.text();
          console.error('Failed to send approval email:', errText);
          toast.success(`Compte approuvé pour ${request.email} (email de confirmation: échec)`);
        } else {
          const result = await approvalResponse.json();
          if (result.emailError) {
            console.error('Email error:', result.emailError);
            toast.success(`Compte approuvé pour ${request.email} (email de confirmation: échec)`);
          } else {
            toast.success(`Compte participant approuvé pour ${request.email}`);
          }
        }
      } else if (request.type === 'member_account') {
        const { error } = await supabase
          .from('participant_accounts')
          .update({ account_status: 'approved' })
          .eq('id', request.account_id || request.id);

        if (error) throw error;

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();
        const approvalResponse = await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'send_approval_email',
            email: request.email,
            name: request.name,
            type: 'participant',
            operation_name: request.operation_name,
          }),
        });

        if (!approvalResponse.ok) {
          toast.success(`Compte approuvé pour ${request.email} (email de confirmation: échec)`);
        } else {
          toast.success(`Compte membre approuvé pour ${request.email}`);
        }
      } else {
        const { error } = await supabase.rpc('process_manager_request', {
          p_admin_id: request.id,
          p_action: 'approve',
        });

        if (error) throw error;

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();
        await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'send_approval_email',
            email: request.email,
            type: 'manager',
            operation_name: request.operation_name,
          }),
        });

        toast.success(`Compte gestionnaire approuve pour ${request.email}`);
      }

      loadOperations();
    } catch (error: any) {
      console.error('Erreur approbation:', error);
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (request: PendingRequest) => {
    if (!confirm(`Refuser la demande de ${request.email} ?`)) return;

    setProcessingRequest(request.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (request.type === 'participant') {
        // Reject all participants with the same email in the same operation (group members)
        const { error } = await supabase
          .from('participants')
          .update({ account_status: 'rejected', email: null })
          .eq('sharing_operation_id', request.operation_id)
          .eq('email', request.email);

        if (error) throw error;
      } else if (request.type === 'member_account') {
        const { error } = await supabase
          .from('participant_accounts')
          .update({ account_status: 'rejected' })
          .eq('id', request.account_id || request.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.rpc('process_manager_request', {
          p_admin_id: request.id,
          p_action: 'reject',
        });

        if (error) throw error;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send_rejection_email',
          email: request.email,
          type: request.type,
          operation_name: request.operation_name,
        }),
      });

      toast.success(`Demande refusee pour ${request.email}`);
      loadOperations();
    } catch (error: any) {
      console.error('Erreur refus:', error);
      toast.error('Erreur lors du refus');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleCreateOperation = async () => {
    if (!newOperationName.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase
        .from('sharing_operations')
        .insert({
          name: newOperationName,
          description: newOperationDescription,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Opération créée avec succès');
      setShowCreateModal(false);
      setNewOperationName('');
      setNewOperationDescription('');
      loadOperations();
    } catch (error: any) {
      console.error('Erreur création opération:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectOperation = (operation: SharingOperation) => {
    localStorage.setItem('selected_sharing_operation_id', operation.id);
    navigate('/admin');
  };

  const handleDeleteOperation = async (operation: SharingOperation) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${operation.name}" ? L'opération sera placée dans la corbeille pendant 1 semaine.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('sharing_operations')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', operation.id);

      if (error) throw error;

      toast.success('Opération mise à la corbeille');
      loadOperations();
    } catch (error: any) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRestoreOperation = async (operation: SharingOperation) => {
    try {
      const { error } = await supabase
        .from('sharing_operations')
        .update({ deleted_at: null })
        .eq('id', operation.id);

      if (error) throw error;

      toast.success('Opération restaurée');
      loadOperations();
    } catch (error: any) {
      console.error('Erreur restauration:', error);
      toast.error('Erreur lors de la restauration');
    }
  };

  const handlePermanentDelete = async (operation: SharingOperation) => {
    if (!confirm(`Supprimer définitivement "${operation.name}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('sharing_operations')
        .delete()
        .eq('id', operation.id);

      if (error) throw error;

      toast.success('Opération supprimée définitivement');
      loadOperations();
    } catch (error: any) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRename = (operation: SharingOperation) => {
    setSelectedOperation(operation);
    setRenameValue(operation.name);
    setShowRenameModal(true);
  };

  const handleSaveRename = async () => {
    if (!renameValue.trim() || !selectedOperation) {
      toast.error('Le nom est requis');
      return;
    }

    setRenaming(true);
    try {
      const { error } = await supabase
        .from('sharing_operations')
        .update({ name: renameValue.trim() })
        .eq('id', selectedOperation.id);

      if (error) throw error;

      toast.success('Opération renommée');
      setShowRenameModal(false);
      setSelectedOperation(null);
      setRenameValue('');
      loadOperations();
    } catch (error: any) {
      console.error('Erreur renommage:', error);
      toast.error('Erreur lors du renommage');
    } finally {
      setRenaming(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await supabase.auth.signOut();
      toast.success('Déconnexion réussie');
      navigate('/');
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Session expirée, veuillez vous reconnecter');
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/database-backup`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Erreur ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_sunisup_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Sauvegarde téléchargée avec succès');
    } catch (error: any) {
      console.error('Backup error:', error);
      toast.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setBackingUp(false);
    }
  };

  const handleOpenPendingForOperation = (operationId: string) => {
    setFilteredOperationId(operationId);
    setShowPendingModal(true);
  };

  const filteredPendingRequests = filteredOperationId
    ? pendingRequests.filter(req => req.operation_id === filteredOperationId)
    : pendingRequests;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F8BD3D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">
              Mes Opérations de Partage
            </h1>
            <p className="text-neutral-600">
              {isSuperAdmin
                ? 'Sélectionnez une opération de partage pour gérer ses participants et données.'
                : 'Sélectionnez l\'opération de partage que vous souhaitez gérer.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isSuperAdmin && (
              <button
                onClick={() => setShowFormulasModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg font-medium transition-colors shadow-sm"
                title="Modifier les formules et taux de facturation"
              >
                <FlaskConical className="w-4 h-4" />
                Formules
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => setShowHelpDoc(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg font-medium transition-colors shadow-sm"
                title="Documentation – Formules et logiques"
              >
                <HelpCircle className="w-4 h-4" />
                Aide
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => setShowSimulation(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg font-medium transition-colors shadow-sm"
                title="Simulateur d'énergie"
              >
                <Calculator className="w-4 h-4" />
                Simulation
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => setShowCRM(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg font-medium transition-colors shadow-sm"
                title="CRM – Gestion des membres et communications"
              >
                <LayoutDashboard className="w-4 h-4" />
                CRM
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => setShowAccessModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg font-medium transition-colors shadow-sm"
                title="Gérer les accès gestionnaires et simulateur"
              >
                <KeyRound className="w-4 h-4" />
                Gérer accès
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={handleBackup}
                disabled={backingUp}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Télécharger une sauvegarde complète de la base de données"
              >
                <Download className={`w-4 h-4 ${backingUp ? 'animate-pulse' : ''}`} />
                {backingUp ? 'Sauvegarde...' : 'Sauvegarde'}
              </button>
            )}
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="flex items-center gap-2 px-4 py-2 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <LogOut className="w-4 h-4" />
              {isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {operations.map((operation) => (
            <div
              key={operation.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#F8BD3D] to-[#E5A935] rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-neutral-900" />
                  </div>
                  {isSuperAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRename(operation);
                        }}
                        className="p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                        title="Renommer"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOperation(operation);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold text-neutral-900 mb-2">
                  {operation.name}
                </h3>

                {operation.description && (
                  <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                    {operation.description}
                  </p>
                )}

                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-neutral-600">
                    <Users className="w-4 h-4 mr-2" />
                    {operation.participants_count} participant{operation.participants_count !== 1 ? 's' : ''}
                  </div>
                  {isSuperAdmin && operation.pending_requests_count && operation.pending_requests_count > 0 ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPendingForOperation(operation.id);
                      }}
                      className="flex items-center text-sm text-amber-600 hover:text-amber-700 hover:underline transition-all"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      {operation.pending_requests_count} demande{operation.pending_requests_count !== 1 ? 's' : ''} en attente
                    </button>
                  ) : null}
                  <div className="flex items-center text-sm text-neutral-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    Créée le {new Date(operation.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleSelectOperation(operation)}
                    className="flex-1 px-4 py-2 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 rounded-lg font-medium transition-colors"
                  >
                    Ouvrir
                  </button>
                </div>
              </div>
            </div>
          ))}

          {isSuperAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-dashed border-neutral-300 hover:border-[#F8BD3D] p-6 flex flex-col items-center justify-center min-h-[280px] group"
            >
              <div className="w-16 h-16 bg-neutral-100 group-hover:bg-[#F8BD3D] rounded-full flex items-center justify-center mb-4 transition-colors">
                <Plus className="w-8 h-8 text-neutral-400 group-hover:text-neutral-900 transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Nouvelle Opération
              </h3>
              <p className="text-sm text-neutral-600 text-center">
                Créer une nouvelle opération de partage énergétique
              </p>
            </button>
          )}
        </div>
      </div>

      {/* Modal Création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">
              Nouvelle Opération de Partage
            </h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Nom de l'opération *
                </label>
                <input
                  type="text"
                  value={newOperationName}
                  onChange={(e) => setNewOperationName(e.target.value)}
                  placeholder="Ex: Sun Is UP - Liège"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#F8BD3D] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Description (optionnel)
                </label>
                <textarea
                  value={newOperationDescription}
                  onChange={(e) => setNewOperationDescription(e.target.value)}
                  placeholder="Description de l'opération..."
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#F8BD3D] focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 bg-neutral-200 hover:bg-neutral-300 text-neutral-900 rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateOperation}
                disabled={creating || !newOperationName.trim()}
                className="flex-1 px-4 py-2 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal Renommer */}
      {showRenameModal && selectedOperation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">
              Renommer l'opération
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Nouveau nom
              </label>
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Nom de l'opération"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#F8BD3D] focus:border-transparent"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRenameModal(false);
                  setSelectedOperation(null);
                  setRenameValue('');
                }}
                className="flex-1 px-4 py-2 bg-neutral-200 hover:bg-neutral-300 text-neutral-900 rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveRename}
                disabled={renaming || !renameValue.trim()}
                className="flex-1 px-4 py-2 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {renaming ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Corbeille */}
      {showTrashModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-neutral-900">
                Corbeille
              </h2>
              <button
                onClick={() => setShowTrashModal(false)}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-neutral-500 mb-4">
              Les opérations supprimées sont conservées pendant 7 jours avant d'être définitivement effacées.
            </p>

            <div className="space-y-3">
              {deletedOperations.length === 0 ? (
                <p className="text-center text-neutral-500 py-8">
                  La corbeille est vide
                </p>
              ) : (
                deletedOperations.map((operation) => {
                  const deletedDate = operation.deleted_at ? new Date(operation.deleted_at) : new Date();
                  const expiresDate = new Date(deletedDate);
                  expiresDate.setDate(expiresDate.getDate() + 7);
                  const daysLeft = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

                  return (
                    <div
                      key={operation.id}
                      className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900">{operation.name}</p>
                          <p className="text-sm text-neutral-500">
                            {operation.participants_count} participant{operation.participants_count !== 1 ? 's' : ''} -
                            Expire dans {daysLeft} jour{daysLeft !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRestoreOperation(operation)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Restaurer"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(operation)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer définitivement"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}


      {/* Modal Demandes en attente */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-neutral-900">
                  Demandes en attente
                </h2>
                {filteredOperationId && (
                  <p className="text-sm text-neutral-500 mt-1">
                    {operations.find(op => op.id === filteredOperationId)?.name}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowPendingModal(false);
                  setFilteredOperationId(null);
                }}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                X
              </button>
            </div>

            {filteredOperationId && (
              <button
                onClick={() => setFilteredOperationId(null)}
                className="text-sm text-amber-600 hover:text-amber-700 mb-4 underline"
              >
                Voir toutes les demandes
              </button>
            )}

            <p className="text-sm text-neutral-500 mb-4">
              Validez ou refusez les demandes de creation de compte pour vos operations de partage.
            </p>

            <div className="space-y-3">
              {filteredPendingRequests.length === 0 ? (
                <p className="text-center text-neutral-500 py-8">
                  Aucune demande en attente
                </p>
              ) : (
                filteredPendingRequests.map((request) => (
                  <div
                    key={`${request.type}-${request.id}`}
                    className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        request.type === 'manager' ? 'bg-blue-100' : request.type === 'member_account' ? 'bg-teal-100' : 'bg-amber-100'
                      }`}>
                        {request.type === 'manager' ? (
                          <Building2 className={`w-5 h-5 text-blue-600`} />
                        ) : request.type === 'member_account' ? (
                          <User className={`w-5 h-5 text-teal-600`} />
                        ) : (
                          <User className={`w-5 h-5 text-amber-600`} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-neutral-900">{request.email}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            request.type === 'manager'
                              ? 'bg-blue-100 text-blue-700'
                              : request.type === 'member_account'
                              ? 'bg-teal-100 text-teal-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {request.type === 'manager' ? 'Gestionnaire' : request.type === 'member_account' ? 'Compte membre' : 'Participant'}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-500">
                          {request.operation_name}
                          {request.name && ` - ${request.name}`}
                          {request.ean_code && ` (EAN: ${request.ean_code})`}
                        </p>
                        <p className="text-xs text-neutral-400">
                          Demande le {new Date(request.request_date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApproveRequest(request)}
                        disabled={processingRequest === request.id}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Approuver"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleRejectRequest(request)}
                        disabled={processingRequest === request.id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Refuser"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bouton Demandes en attente flottant */}
      {isSuperAdmin && pendingRequests.length > 0 && (
        <button
          onClick={() => {
            setFilteredOperationId(null);
            setShowPendingModal(true);
          }}
          className="fixed bottom-6 right-24 w-14 h-14 bg-amber-100 hover:bg-amber-200 text-amber-600 rounded-full shadow-lg flex items-center justify-center transition-colors"
          title="Voir les demandes en attente"
        >
          <Bell className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
            {pendingRequests.length}
          </span>
        </button>
      )}

      {/* Bouton Corbeille flottant */}
      {isSuperAdmin && deletedOperations.length > 0 && (
        <button
          onClick={() => setShowTrashModal(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-red-100 hover:bg-red-200 text-red-600 rounded-full shadow-lg flex items-center justify-center transition-colors"
          title="Voir la corbeille"
        >
          <Trash2 className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {deletedOperations.length}
          </span>
        </button>
      )}

      <AccessManagementModal
        isOpen={showAccessModal}
        onClose={() => setShowAccessModal(false)}
        operations={operations}
        isSuperAdmin={isSuperAdmin}
      />

      <FormulasModal
        isOpen={showFormulasModal}
        onClose={() => setShowFormulasModal(false)}
      />

      {showHelpDoc && (
        <HelpDocModal onClose={() => setShowHelpDoc(false)} />
      )}

      <CRMModal
        isOpen={showCRM}
        onClose={() => setShowCRM(false)}
        operationId={filteredOperationId}
        isSuperAdmin={isSuperAdmin}
      />

      <SimulatorModal
        isOpen={showSimulation}
        onClose={() => setShowSimulation(false)}
        isSuperAdmin={isSuperAdmin}
      />
    </div>
  );
}
